import { useState, useEffect } from "react";

const ATR_COLORS = {
  newMoon: "#00313C",
  turquoise: "#009A84",
  green: "#56C177",
  lightBlue: "#99D6EA",
};

const ATR_GRADIENT = `linear-gradient(135deg, ${ATR_COLORS.turquoise} 0%, ${ATR_COLORS.green} 50%, ${ATR_COLORS.lightBlue} 100%)`;

function AnimatedStat({
  target,
  duration,
  active,
  decimals,
  suffix,
  prefix,
}: {
  target: number;
  duration: number;
  active: boolean;
  decimals: number;
  suffix: string;
  prefix?: string;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setCount(0);
      return;
    }
    let start: number | null = null;
    let raf: number;
    const stepFn = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(target * eased);
      if (p < 1) raf = requestAnimationFrame(stepFn);
    };
    raf = requestAnimationFrame(stepFn);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);

  return (
    <>
      {prefix || ""}
      {count.toFixed(decimals)}
      {suffix}
    </>
  );
}

export default function AirSharePanel() {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [dashOffset, setDashOffset] = useState(0);

  useEffect(() => {
    let raf: number;
    const animate = () => {
      setDashOffset((d) => (d - 0.35) % 14);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const data: [number, number][] = [
    [0, 6.7],
    [30, 4.6],
    [60, 2.8],
    [90, 2.2],
    [120, 2.2],
  ];

  const W = 620;
  const H = 380;
  const PAD = { l: 72, r: 30, t: 44, b: 56 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const minX = 0,
    maxX = 120,
    minY = 0,
    maxY = 8;

  const toSvgX = (x: number) => PAD.l + ((x - minX) / (maxX - minX)) * chartW;
  const toSvgY = (y: number) => PAD.t + chartH - ((y - minY) / (maxY - minY)) * chartH;

  const pathD = data
    .map(
      ([x, y], i) =>
        `${i === 0 ? "M" : "L"}${toSvgX(x).toFixed(1)},${toSvgY(y).toFixed(1)}`
    )
    .join(" ");

  const areaD =
    pathD +
    ` L${toSvgX(120)},${PAD.t + chartH} L${toSvgX(0)},${PAD.t + chartH} Z`;

  const hovered = hoverIdx !== null ? data[hoverIdx] : null;

  const getColor = (val: number) => {
    if (val > 5) return ATR_COLORS.turquoise;
    if (val > 3) return ATR_COLORS.green;
    return ATR_COLORS.lightBlue;
  };

  const startPoint = data[0];
  const midPoint = data[3];

  const arrowX = toSvgX(90) + 45;
  const yBottom = toSvgY(2.2) - 2;
  const yTop = toSvgY(6.7) - 2;
  const midY = (yTop + yBottom) / 2;

  return (
    <div
      style={{
        width: W,
        fontFamily: "'ATR Innova', sans-serif",
        padding: '4px 0',
      }}
    >
      {/* ── EN-TÊTE AVEC LE STYLE DE LA PAGE 4 ── */}
      <div style={{ marginBottom: 20, paddingLeft: 4 }}>
        {/* 
          ═══════════════════════════════════════════════════════
          TITRE PRINCIPAL — STYLE IDENTIQUE À LA PAGE 4
          ═══════════════════════════════════════════════════════
        */}
        <div
          style={{
            fontSize: "clamp(10px, 1vw, 13px)",
            letterSpacing: 1,
            color: "rgba(255,255,255,0.8)",
            textTransform: "uppercase",
            marginBottom: 6,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Probability of flying by drive time
        </div>

        {/* SOUS-TITRE (comme sur la page 4) */}
        <div
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.55)",
            marginBottom: 0,
          }}
        >
          Average air travel demand vs. drive time to nearest airport
        </div>
      </div>

      {/* ── HOVER READOUT ── */}
      <div
        style={{
          height: 38,
          display: "flex",
          alignItems: "center",
          marginBottom: 6,
          paddingLeft: 4,
        }}
      >
        {hovered ? (
          <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: "white",
              letterSpacing: -0.5,
            }}>
              {hovered[0]} min
            </span>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: getColor(hovered[1]),
            }}>
              {hovered[1]}% air share
            </span>
          </div>
        ) : (
          <span style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.2)",
            letterSpacing: 0.3,
          }}>
            ← Hover the chart to explore values
          </span>
        )}
      </div>

      {/* ── SVG ── */}
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: "block" }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="asp-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ATR_COLORS.turquoise} stopOpacity="0.35" />
            <stop offset="50%" stopColor={ATR_COLORS.green} stopOpacity="0.12" />
            <stop offset="100%" stopColor={ATR_COLORS.lightBlue} stopOpacity="0.02" />
          </linearGradient>

          <linearGradient id="asp-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={ATR_COLORS.turquoise} />
            <stop offset="50%" stopColor={ATR_COLORS.green} />
            <stop offset="100%" stopColor={ATR_COLORS.lightBlue} />
          </linearGradient>

          <linearGradient id="arrow-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ATR_COLORS.turquoise} />
            <stop offset="100%" stopColor={ATR_COLORS.green} />
          </linearGradient>

          <filter id="shadow-line">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor={ATR_COLORS.turquoise} floodOpacity="0.3" />
          </filter>

          <filter id="badge-glow">
            <feDropShadow dx="0" dy="4" stdDeviation="12" floodColor={ATR_COLORS.turquoise} floodOpacity="0.3" />
          </filter>

          <marker
            id="arrow-top"
            markerWidth="10"
            markerHeight="10"
            refX="5"
            refY="5"
            orient="0"
          >
            <path d="M2,8 L5,2 L8,8 Z" fill={ATR_COLORS.turquoise} />
          </marker>
          <marker
            id="arrow-bottom"
            markerWidth="10"
            markerHeight="10"
            refX="5"
            refY="5"
            orient="0"
          >
            <path d="M2,2 L5,8 L8,2 Z" fill={ATR_COLORS.turquoise} />
          </marker>
        </defs>

        {/* ── GRILLE Y ── */}
        {[0, 2, 4, 6, 8].map((v) => (
          <g key={v}>
            <line
              x1={PAD.l}
              y1={toSvgY(v)}
              x2={W - PAD.r}
              y2={toSvgY(v)}
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
            <text
              x={PAD.l - 10}
              y={toSvgY(v) + 5}
              fontSize="12"
              fill="rgba(255,255,255,0.35)"
              textAnchor="end"
              fontFamily="ATR Innova"
            >
              {v}%
            </text>
          </g>
        ))}

        {/* ── AXE Y LABEL ── */}
        <text
          x={16}
          y={PAD.t + chartH / 2}
          fontSize="11"
          fill="rgba(255,255,255,0.2)"
          textAnchor="middle"
          fontFamily="ATR Innova"
          letterSpacing="0.5"
          transform={`rotate(-90, 16, ${PAD.t + chartH / 2})`}
        >
          Air modal share (%)
        </text>

        {/* ── AXE X ── */}
        {[0, 30, 60, 90, 120].map((v) => (
          <text
            key={v}
            x={toSvgX(v)}
            y={H - PAD.b + 24}
            fontSize="12"
            fill="rgba(255,255,255,0.35)"
            textAnchor="middle"
            fontFamily="ATR Innova"
          >
            {v}
          </text>
        ))}

        {/* ── AXE X LABEL ── */}
        <text
          x={PAD.l + chartW / 2}
          y={H - 8}
          fontSize="11"
          fill="rgba(255,255,255,0.2)"
          textAnchor="middle"
          fontFamily="ATR Innova"
          letterSpacing="0.5"
        >
          Drive time to airport (minutes)
        </text>

        {/* ── LIGNE DE RÉFÉRENCE À 2.2% ── */}
        <line
          x1={PAD.l}
          y1={toSvgY(2.2)}
          x2={W - PAD.r}
          y2={toSvgY(2.2)}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1"
          strokeDasharray="5,4"
        />

        {/* ── ZONE ── */}
        <path d={areaD} fill="url(#asp-area)" />

        {/* ── COURBE ── */}
        <path
          d={pathD}
          fill="none"
          stroke="url(#asp-line)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#shadow-line)"
        />

        {/* ── FLÈCHE AVEC LIGNE DE FOND CONTINUE ── */}
        <g>
          {/* Ligne de fond continue */}
          <line
            x1={arrowX}
            y1={yTop + 6}
            x2={arrowX}
            y2={yBottom - 6}
            stroke={ATR_COLORS.turquoise}
            strokeWidth="2"
            opacity="0.15"
          />

          {/* Ligne pointillée avec pointes de flèche */}
          <line
            x1={arrowX}
            y1={yTop}
            x2={arrowX}
            y2={yBottom}
            stroke="url(#arrow-grad)"
            strokeWidth="2.5"
            strokeDasharray="6,4"
            strokeDashoffset={dashOffset}
            markerStart="url(#arrow-top)"
            markerEnd="url(#arrow-bottom)"
            filter="drop-shadow(0 0 8px rgba(0, 154, 132, 0.3))"
          />

          {/* Points décoratifs aux extrémités */}
          <circle cx={arrowX} cy={yTop + 2} r="3" fill={ATR_COLORS.turquoise} opacity="0.5" />
          <circle cx={arrowX} cy={yBottom - 2} r="3" fill={ATR_COLORS.green} opacity="0.5" />

          {/* Badge ×3 */}
          <rect
            x={arrowX - 28}
            y={midY - 20}
            width={56}
            height={38}
            rx={18}
            fill={ATR_COLORS.newMoon}
            fillOpacity="0.92"
            stroke={ATR_COLORS.turquoise}
            strokeWidth="2"
            filter="url(#badge-glow)"
          />
          <text
            x={arrowX}
            y={midY + 6}
            fontSize="18"
            fontWeight="900"
            fill={ATR_COLORS.turquoise}
            textAnchor="middle"
            fontFamily="ATR Innova"
            style={{
              letterSpacing: 1.5,
              textShadow: `0 0 20px ${ATR_COLORS.turquoise}40`,
            }}
          >
            ×3
          </text>
        </g>

        {/* ── BADGE 6.7% (Turquoise) ── */}
        <g>
          <rect
            x={toSvgX(startPoint[0]) + 12}
            y={toSvgY(startPoint[1]) - 35}
            width={60}
            height={28}
            rx={14}
            fill={`${ATR_COLORS.turquoise}25`}
            stroke={ATR_COLORS.turquoise}
            strokeOpacity="0.5"
          />
          <text
            x={toSvgX(startPoint[0]) + 42}
            y={toSvgY(startPoint[1]) - 17}
            fontSize="16"
            fontWeight="800"
            fill={ATR_COLORS.turquoise}
            textAnchor="middle"
            fontFamily="ATR Innova"
          >
            6.7%
          </text>
        </g>

        {/* ── BADGE 2.2% (Light Blue) ── */}
        <g>
          <rect
            x={toSvgX(midPoint[0]) - 30}
            y={toSvgY(midPoint[1]) - 40}
            width={60}
            height={28}
            rx={14}
            fill={`${ATR_COLORS.lightBlue}25`}
            stroke={ATR_COLORS.lightBlue}
            strokeOpacity="0.5"
          />
          <text
            x={toSvgX(midPoint[0])}
            y={toSvgY(midPoint[1]) - 22}
            fontSize="16"
            fontWeight="800"
            fill={ATR_COLORS.lightBlue}
            textAnchor="middle"
            fontFamily="ATR Innova"
          >
            2.2%
          </text>
        </g>

        {/* ── POINTS ── */}
        {data.map(([x, y], i) => (
          <g key={i}>
            <circle
              cx={toSvgX(x)}
              cy={toSvgY(y)}
              r={hoverIdx === i ? 9 : 6}
              fill={ATR_COLORS.newMoon}
              stroke={getColor(y)}
              strokeWidth="2.5"
              style={{ transition: "r 0.15s ease" }}
            />
            {hoverIdx === i && (
              <circle
                cx={toSvgX(x)}
                cy={toSvgY(y)}
                r={18}
                fill="none"
                stroke={getColor(y)}
                strokeWidth="1.5"
                opacity="0.3"
              />
            )}
            <rect
              x={toSvgX(x) - chartW / data.length / 2}
              y={PAD.t}
              width={chartW / data.length}
              height={chartH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
            />
          </g>
        ))}
      </svg>

      {/* ── SOURCE ── */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: "1px solid rgba(255,255,255,0.04)",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.3)",
          letterSpacing: 0.3,
        }}>
          Air share drops <strong style={{ color: ATR_COLORS.turquoise }}>3×</strong> beyond 90 min drive time
        </span>
        <span style={{
          fontSize: 9,
          color: "rgba(255,255,255,0.12)",
          letterSpacing: 0.5,
        }}>
          Source: ATR MobilityMonitor · 2022–2023
        </span>
      </div>
    </div>
  );
}